/* Ações (Painel Administrativo) — os jobs agendados por empresa.
 *
 * Ligada ao real `/api/scheduled-actions` (lista + jobs disponíveis) e ao PUT
 * que só liga/desliga e ajusta o intervalo. Um job cujo nome não existe no
 * catálogo aparece marcado como "sem job" — é o que acontece quando alguém
 * digita errado. Nebula. */

import React, { useEffect, useState } from 'react';

import { api } from '../api/cliente';
import { useRecurso } from '../estado/recurso';
import { duracao } from './formato';
import { Aviso, Cabecalho, Icon, Nota, Secao, Tag } from './ui';

const th = {
  position: 'sticky', top: 0, textAlign: 'left', padding: '12px 20px',
  background: 'var(--surface-card)', font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)',
  color: 'var(--text-muted)', letterSpacing: 'var(--ls-wide)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap',
};
const td = { padding: '12px 20px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' };

export default function Acoes() {
  const recurso = useRecurso(
    (sinal) => Promise.all([api.acoes(sinal), api.assistentes(sinal)]).then(([a, e]) => ({
      itens: a?.itens || [], jobs: a?.jobs_disponiveis || [], empresas: e?.itens || [],
    })),
    [],
  );
  const d = recurso.dados;
  const nomePorSender = {};
  for (const e of d?.empresas || []) nomePorSender[e.sender] = e.nome;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho titulo="Ações" descricao="Os jobs agendados de cada empresa. Você liga, desliga e ajusta o intervalo." />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 8px' }}>
        <Secao titulo={recurso.carregando ? 'Carregando…' : `${(d?.itens || []).length} ${(d?.itens || []).length === 1 ? 'ação' : 'ações'}`}>
          {recurso.erro ? (
            <Aviso titulo="As ações não carregaram" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} />
          ) : !recurso.carregando && (d?.itens || []).length === 0 ? (
            <Aviso titulo="Nenhuma ação agendada" texto="As ações agendadas das empresas aparecem aqui." />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>
              <thead>
                <tr><th style={th}>Empresa</th><th style={th}>Ação</th><th style={th}>Intervalo</th><th style={th}>Estado</th></tr>
              </thead>
              <tbody>
                {(d?.itens || []).map((a) => (
                  <Linha key={a.id} acao={a} empresa={nomePorSender[a.sender] || a.sender?.split('@')[0]} temJob={(d?.jobs || []).includes(a.acao)} aoMudar={recurso.recarregar} />
                ))}
              </tbody>
            </table>
          )}
        </Secao>
        <Nota>Fonte: /api/scheduled-actions. O que cada job faz é código; aqui só se liga/desliga e ajusta o ritmo.</Nota>
      </div>
    </div>
  );
}

function Linha({ acao, empresa, temJob, aoMudar }) {
  const [intervalo, setIntervalo] = useState(acao.intervalo_segundos);
  const [ativa, setAtiva] = useState(acao.ativa);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { setIntervalo(acao.intervalo_segundos); setAtiva(acao.ativa); }, [acao]);

  const mudou = intervalo !== acao.intervalo_segundos;

  async function salvar(proximaAtiva, proximoIntervalo) {
    setSalvando(true);
    try {
      await api.salvarAcao(acao.id, { ativa: proximaAtiva, intervalo: proximoIntervalo });
      aoMudar?.();
    } finally {
      setSalvando(false);
    }
  }

  return (
    <tr>
      <td style={{ ...td, color: 'var(--text-primary)' }}>{empresa}</td>
      <td style={td}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-label)', color: 'var(--text-secondary)' }}>
          {acao.acao}
          {!temJob && <Tag tone="danger">sem job</Tag>}
        </span>
      </td>
      <td style={td}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <input type="number" min="1" value={intervalo} onChange={(e) => setIntervalo(Number(e.target.value))} style={{ width: 90, height: 32, padding: '0 8px', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-label)', outline: 'none' }} />
          <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>s · {duracao(intervalo)}</span>
          {mudou && (
            <button type="button" onClick={() => salvar(ativa, intervalo)} disabled={salvando} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 28, padding: '0 8px', borderRadius: 'var(--radius-md)', border: 0, cursor: 'pointer', background: 'var(--accent)', color: 'var(--accent-on)', font: 'var(--type-caption)', fontWeight: 'var(--fw-semibold)' }}><Icon name="check" size={12} /> Salvar</button>
          )}
        </span>
      </td>
      <td style={td}>
        <button type="button" onClick={() => { const p = !ativa; setAtiva(p); salvar(p, intervalo); }} disabled={salvando} title="Ligar/desligar" style={{ cursor: 'pointer', border: 0, background: 'transparent', padding: 0 }}>
          <Tag tone={ativa ? 'success' : 'neutral'} dot={ativa}>{ativa ? 'Ativa' : 'Pausada'}</Tag>
        </button>
      </td>
    </tr>
  );
}
